import { assertUniqueDoorIds, changeDoor, editDoorCells, insertDoor, withdrawDoor, type DoorCellChange, type DoorState } from "./doors.ts";
import type { DoorDefinition, DoorLayer, MapDocument } from "./types.ts";

/**
 * The door commands (ADR-122 decision 2.1), each over one door layer of one map: `paintDoor`, one a stroke, each
 * cell's door id before and after; `addDoor` and `removeDoor`, a door put in or taken out of the layer's list at
 * its index, each the other's inverse, a removal refused while a cell holds the door; and `updateDoor`, a door's
 * name, destination and fields before and after. A fill of a door layer is the shared `fillCells`.
 */
export type DoorCommand =
  | { readonly kind: "paintDoor"; readonly mapId: string; readonly layerId: string; readonly cells: readonly DoorCellChange[] }
  | { readonly kind: "addDoor"; readonly mapId: string; readonly layerId: string; readonly index: number; readonly door: DoorDefinition }
  | { readonly kind: "removeDoor"; readonly mapId: string; readonly layerId: string; readonly index: number; readonly door: DoorDefinition }
  | { readonly kind: "updateDoor"; readonly mapId: string; readonly layerId: string; readonly doorId: string; readonly before: DoorState; readonly after: DoorState };

const DOOR_KINDS: ReadonlySet<string> = new Set(["paintDoor", "addDoor", "removeDoor", "updateDoor"]);

/**
 * Whether a command is a door command.
 *
 * @param command The command.
 * @returns True for `paintDoor`, `addDoor`, `removeDoor` and `updateDoor`.
 */
export function isDoorCommand(command: { readonly kind: string }): command is DoorCommand {
  return DOOR_KINDS.has(command.kind);
}

/**
 * The command that exactly reverses a door command.
 *
 * @param command The command.
 * @returns Its inverse.
 */
export function invertDoorCommand(command: DoorCommand): DoorCommand {
  switch (command.kind) {
    case "paintDoor":
      return { ...command, cells: command.cells.map((c) => ({ index: c.index, before: c.after, after: c.before })) };
    case "addDoor":
      return { ...command, kind: "removeDoor" };
    case "removeDoor":
      return { ...command, kind: "addDoor" };
    case "updateDoor":
      return { ...command, before: command.after, after: command.before };
  }
}

/**
 * Applies a door command to the map it targets.
 *
 * @param map The map.
 * @param command The command.
 * @returns The map with the command applied.
 * @throws Error when the layer is missing or not a door layer, an added door's id is already the map's, or the
 *   layer refuses the edit (`editDoorCells`, `insertDoor`, `withdrawDoor`, `changeDoor`).
 */
export function applyDoorCommand(map: MapDocument, command: DoorCommand): MapDocument {
  const index = map.layers.findIndex((layer) => layer.id === command.layerId);
  const layer = map.layers[index];
  if (layer === undefined) {
    throw new Error(`layer ${command.layerId} does not exist in map ${map.id}`);
  }
  if (layer.kind !== "door") {
    throw new Error(`layer ${command.layerId} is ${layer.kind}, not door`);
  }
  const edited = editLayer(layer, command);
  const updated = { ...map, layers: map.layers.map((l, i) => (i === index ? edited : l)) };
  if (command.kind === "addDoor") {
    assertUniqueDoorIds(updated, `map ${map.id}`);
  }
  return updated;
}

/**
 * Applies a door command to its layer.
 *
 * @param layer The door layer.
 * @param command The command.
 * @returns The layer edited.
 */
function editLayer(layer: DoorLayer, command: DoorCommand): DoorLayer {
  switch (command.kind) {
    case "paintDoor":
      return editDoorCells(layer, command.cells);
    case "addDoor":
      return insertDoor(layer, command.index, command.door);
    case "removeDoor":
      return withdrawDoor(layer, command.index, command.door);
    case "updateDoor":
      return changeDoor(layer, command.doorId, command.before, command.after);
  }
}
